import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ArrowRight, Play, Users, Smartphone, Star } from 'lucide-react';

interface LandingHeroProps {
  pricingEnabled: boolean;
}

const LandingHero = ({ pricingEnabled }: LandingHeroProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <section className="relative min-h-[90vh] sm:min-h-0 pt-20 pb-20 sm:pt-32 sm:pb-36 px-4 flex items-center">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 start-1/2 -translate-x-1/2 w-[800px] h-[800px] max-w-[100vw] rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute top-1/3 start-1/4 w-[400px] h-[400px] max-w-[80vw] rounded-full bg-accent/5 blur-3xl" />
      </div>
      <div className="relative max-w-4xl mx-auto text-center flex flex-col items-center gap-8 sm:gap-12 w-full">
        <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-muted/50 px-4 py-1.5 text-xs font-medium text-muted-foreground">
          <Smartphone className="h-3.5 w-3.5" />
          {t('landing.installBadge')}
        </div>
        <div className="space-y-5">
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1]">
            {t('landing.heroHeadline')}
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {t('landing.heroSub')}
          </p>
        </div>
        <div className="flex flex-col items-center gap-3 w-full sm:w-auto">
          <Button
            size="lg"
            className="w-full sm:w-auto sm:px-14 min-h-[48px] h-14 text-base rounded-full shadow-xl shadow-primary/30"
            onClick={() => navigate('/auth')}
          >
            {t('landing.heroCta')}
          </Button>
          <p className="text-xs text-muted-foreground">{t('landing.heroCtaMicro')}</p>
        </div>

        {/* Hero testimonial */}
        <div className="max-w-xl w-full rounded-2xl border border-border/60 bg-card/70 backdrop-blur-md px-5 py-4 shadow-lg text-start">
          <div className="flex gap-1 mb-2">
            {[...Array(5)].map((_, j) => (
              <Star key={j} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
            ))}
          </div>
          <p className="text-sm sm:text-base italic leading-relaxed">"{t('landing.heroTestimonialQuote')}"</p>
          <p className="text-xs text-muted-foreground mt-2 font-medium">— {t('landing.heroTestimonialAuthor')}</p>
        </div>

        <div className="flex items-center gap-5 text-sm whitespace-nowrap flex-wrap justify-center">
          <button
            onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors min-h-[44px]"
          >
            <Play className="h-3.5 w-3.5" />
            {t('landing.watchDemo')}
          </button>
          <span className="h-3 w-px bg-border" />
          <button
            onClick={() => navigate('/kid-login')}
            className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors min-h-[44px]"
          >
            <Users className="h-3.5 w-3.5" />
            {t('landing.kidEntry')}
          </button>
        </div>
      </div>
    </section>
  );
};

export default LandingHero;
